import { useMemo } from "react"
import { dateFormat, numberFormat, round } from "shared/util"
import { useSearch, useTargetList } from "features/search/context"

import {
  Box, Stack, StackProps, List, Typography,
  Chip,
  Rating,
  //Checkbox
} from "@mui/material"

import { Section } from 'components/controls/section'
//import { LimittedList } from "components/controls/limittedList"
import { BlockCaption } from "components/controls/blockCaption"
import { ValueSpan } from "components/controls/valueSpan"

import { TargetListRecordAnnotateForm } from "features/kojin/components/targetListRecordAnnotationForm"
import { OrganListItem } from "features/kojin/components/organListItem"
import { SenmoniText } from "features/kojin/components/senmoniText"

import { KojinGuideLines } from "./components/kojinGuideLines"
import { KojinMetaTags } from "./components/kojinMetaTags"

import { useKojinPreview } from "../../container"

import { useUserSession } from "features/session/context"
import { KanriBunruiText } from "features/kojin/components/kanriBunruiText"
import { MailAddress } from "features/kojin/components/mailAddress"
import { KojinDiseasesBlock } from "./components/diseaseBlock"

//const limit_candidates_count = 3

type KojinBasicPageProps = {
  container: ReturnType<typeof useKojinPreview>
  isSmallMedia: boolean
} & StackProps
export const KojinBasicPage = ({
  container: {
    kojin,
    onSisetuOpen,
    onChangeAnnotate,
    //author,
    //onSelectCandidate
  },
  isSmallMedia,
  ...others
}: KojinBasicPageProps) => {
  const { isCareNet } = useUserSession()
  const search = useSearch()
  const targetList = useTargetList()

  const sectionWidth = isSmallMedia ? '5rem' : undefined
  const { categories, scores } = useMemo(
    () => {
      const categories = targetList?.category?.flatMap(({ title, sequence }) => {
        const category = kojin?.target?.category?.find(cat => cat.sequence === sequence)?.name
        return category ? { title, value: category } : []
      }) ?? []
      const scores = targetList?.score?.flatMap(({ title, sequence }) => {
        const score = kojin?.target?.score?.find(cat => cat.sequence === sequence)?.score
        return score ? { title, score } : []
      }) ?? []
      return { categories, scores }
    },
    [kojin, targetList]
  )
  return (
    <>
      <Stack
        direction='column'
        position="relative"
        overflow="auto"
        height="100%"
        boxSizing="border-box"
        {...others}
      >
        {(onChangeAnnotate && search && targetList && kojin?.target?.sourceType === 1) && (
          <TargetListRecordAnnotateForm
            searchId={search.searchId}
            targetListId={targetList.listId}
            kojin={kojin}
            onChangeAnnotate={onChangeAnnotate}
          />
        )}

        {/*(onSelectCandidate && author && (author.candidates?.length ?? 0) > 1) && (
          <Box>
            <BlockCaption caption='候補医師'/>
            <LimittedList
              keyPrefix='candidate'
              list={author.candidates ?? []}
              count={limit_candidates_count}
              record={(candidate, i) =>
                <Stack direction='row' alignItems='flex-start' width='100%'>
                  <Checkbox
                    checked={candidate.kojinCode === author.kojinCode}
                    size="small"
                    onChange={() => {
                      if (candidate.kojinCode === author.kojinCode) {
                        onSelectCandidate(null)
                      } else {
                        onSelectCandidate(candidate.kojinCode)
                      }
                    }}
                  />           
                  <Stack direction='column' flexGrow={1} paddingTop={1}>
                    <Stack direction='row' spacing={1} flexWrap="wrap" alignItems="baseline">
                      <Typography variant='body2' color='text.primary'>{candidate.isiName}</Typography>
                      { candidate.nenrei && <Typography variant='caption' color='text.secondary'>{candidate.nenrei}歳</Typography> }
                      { candidate.seibetuCode && <Typography variant='caption' color='text.secondary'>{candidate.seibetuCode === '1' ? '男性' : candidate.seibetuCode === '2' ? '女性' : 'その他／不明'}</Typography> }
                      { candidate.sotukou && <Typography variant='caption' color={'text.secondary'}>{candidate.sotukou}</Typography> }
                      <Box flexGrow={1} />
                      <Typography variant='caption' color='text.secondary' whiteSpace='nowrap'>{candidate.score} pt.</Typography>
                    </Stack>
                    { candidate.sisetu && candidate.sisetu.length > 0 && (
                      <Stack direction='column' borderTop='1px solid #dcdcdc'>
                        { candidate.sisetu?.map((sisetu, j) => 
                          <Typography key={`candidate-${i}-sisetu-${j}`} variant='caption' color={'text.secondary'}>{sisetu.sisetuName}</Typography>
                        ) }
                      </Stack>
                    ) }
                  </Stack>
                </Stack>
              }
            />
          </Box>
        )*/}
        
        { kojin && (
          <Box>
            <BlockCaption
              caption='医師情報'
              rightElement={
                kojin.kojinCode ? (
                  <Typography variant="caption" color='text.primary' component="div">DCF個人コード: {kojin.kojinCode}</Typography>
                ) : <></>
              }
            />
            { (kojin.seinenGappi || kojin.nenrei) && (
              <Section labelWidth={sectionWidth} label="年齢/性別">
                <Stack direction='row' spacing={1} flexWrap="wrap" useFlexGap>
                  {kojin.seinenGappi && <Typography variant="body2" color='text.primary' component="div">{dateFormat(kojin.seinenGappi, { suffix: '生まれ' })}</Typography>}
                  {kojin.nenrei && <Typography variant="body2" color='text.primary' component="div">{kojin.nenrei}歳</Typography>}
                  <Typography variant="body2" color='text.primary' component="div">{kojin.seibetuCode === '1' ? '男性' : kojin.seibetuCode === '2' ? '女性' : 'その他／不明'}</Typography>
                </Stack>
              </Section>
            ) }
            
            { kojin.syussinchiName ? (
              <Section labelWidth={sectionWidth} label="出身地">
                <Typography variant="body2" color='text.primary' component="div">{kojin.syussinchiName}</Typography>
              </Section>
            ) : <></> }

            { kojin.sotukouName ? (
              <Section labelWidth={sectionWidth} label="卒業校">
                <Stack direction='row' spacing={1} flexWrap="wrap" useFlexGap>
                  {kojin.sotukouName && <Typography variant="body2" color='text.primary' component="div">{kojin.sotukouName}</Typography>}
                  {kojin.gakubuName && <Typography variant="body2" color='text.primary' component="div">{kojin.gakubuName}</Typography>}
                  {kojin.sotugyouYear && <Typography variant="body2" color='text.primary' component="div">{kojin.sotugyouYear}年卒</Typography>}
                </Stack>
              </Section>
            ) : <></> }

            { kojin.organ && kojin.organ.length > 0 ? (
              <Section labelWidth={sectionWidth} label="所属" disablePadding>
                <List disablePadding>
                  {kojin.organ.map((organ, i) => (
                    <OrganListItem key={`organ-${i}`} organ={organ} onSisetuOpen={onSisetuOpen}/>
                  ))}
                </List>
              </Section>
            ) : <></> }

            {(kojin.sinryoka && kojin.sinryoka.length > 0) && (
              <Section labelWidth={sectionWidth} label="診療科">
                <Stack direction='row' flexWrap="wrap">
                  {kojin.sinryoka.map((sinryoka, i, arr) => (
                    <Typography
                      key={`sinryoka-${i}`}
                      variant="body2"
                      color='text.primary'
                      component="span"
                    >
                      {sinryoka.sinryokaName}
                      {(i < (arr.length - 1)) ? "、" : ""}
                    </Typography>
                  ))}
                </Stack>
              </Section>
            )}

            {(kojin.senmoni && kojin.senmoni.length > 0) && (
              <Section labelWidth={sectionWidth} label="専門医">
                <Stack direction='row' flexWrap="wrap">
                  {kojin.senmoni.map((senmoni, i, arr) => (
                    <SenmoniText
                      key={`senmoni-${i}`}
                      senmoni={senmoni}
                      searchsSenmoni={search?.senmoni}
                      suffix={(i < (arr.length - 1)) ? "、" : ""}
                    />
                  ))}
                </Stack>
              </Section>
            )}

            {(kojin.gakkai && kojin.gakkai.length > 0) && (
              <Section labelWidth={sectionWidth} label="学会">
                <Stack direction='row' flexWrap="wrap">
                  {kojin.gakkai.map((gakkai, i, arr) => (
                    <Typography
                      key={`gakkai-${i}`}
                      variant="body2"
                      color='text.primary'
                      component="span"
                    >
                      {gakkai.gakkaiName}
                      {(i < (arr.length - 1)) ? "、" : ""}
                    </Typography>
                  ))}
                </Stack>
              </Section>
            )}

            { kojin.isikaiName ? (
              <Section labelWidth={sectionWidth} label="医師会">
                <Typography variant="body2" color='text.primary' component="div">{kojin.isikaiName}</Typography>
              </Section>
            ) : <></> }

            { kojin.isiTourokuNo ? (
              <Section labelWidth={sectionWidth} label="医師登録番号">
                <Typography variant="body2" color='text.primary' component="div">{kojin.isiTourokuNo}</Typography>
              </Section>
            ) : <></> }

            { kojin.kaikinCode === '1' ? (
              <Section labelWidth={sectionWidth} label="開業">
                {kojin.kaigyouYear ? (
                  <Typography variant="body2" color='text.primary' component="div">{kojin.kaigyouYear}年</Typography>
                ) : (
                  <Typography variant="body2" component="div">◯</Typography>
                ) }
              </Section>
            ) : <></> }
          </Box>
        )}

        {kojin?.score && (
          <Box>
            <BlockCaption caption="分析結果"/>

            <KojinMetaTags kojin={kojin} labelWidth={sectionWidth}/>

            {kojin.score.journal && (
              <Section labelWidth={sectionWidth} label="論文">
                <Stack direction="row" flexWrap="wrap">
                  {kojin.score.journal.counts.single > 0 && (
                    <ValueSpan
                      text={numberFormat(kojin.score.journal.counts.single)}
                      prefix="単独著者"
                      suffix="件"
                      stackProps={{ paddingRight: 1 }}
                    />
                  )}
                  {kojin.score.journal.counts.first > 0 && (
                    <ValueSpan
                      text={numberFormat(kojin.score.journal.counts.first)}
                      prefix="先頭著者"
                      suffix="件"
                      stackProps={{ paddingRight: 1 }}
                    />
                  )}
                  {kojin.score.journal.counts.second > 0 && (
                    <ValueSpan
                      text={numberFormat(kojin.score.journal.counts.second)}
                      prefix="第二著者"
                      suffix="件"
                      stackProps={{ paddingRight: 1 }}
                    />
                  )}
                  {kojin.score.journal.counts.last > 0 && (
                    <ValueSpan
                      text={numberFormat(kojin.score.journal.counts.last)}
                      prefix="責任著者"
                      suffix="件"
                      stackProps={{ paddingRight: 1 }}
                    />
                  )}
                  <ValueSpan
                    text={numberFormat(kojin.score.journal.counts.total)}
                    prefix="全"
                    suffix="件"
                    stackProps={{ paddingRight: 1 }}
                  />
                </Stack>
              </Section>
            )}

            {(kojin.score.trial?.roles && kojin.score.trial.roles.length > 0) && (
              <Section labelWidth={sectionWidth} label="治験役割">
                <Stack direction="row" flexWrap="wrap">
                  {kojin.score.trial.roles.map((role, i, arr) => (
                    <Typography key={`trial-role-${i}`} variant="body2" color='text.primary' component="span">
                      {role}
                      {(i < (arr.length - 1)) ? "、" : ""}
                    </Typography>
                  ))}
                  <ValueSpan stackProps={{ marginLeft: 1 }} prefix="全" text={numberFormat(kojin.score.trial.trialCount)} suffix="件"/>
                </Stack>
              </Section>
            )}

            <KojinGuideLines kojin={kojin} labelWidth={sectionWidth}/>
          </Box>
        )}

        {(categories.length > 0 || scores.length > 0) && (
          <Box>
            <BlockCaption caption="入力情報"/>

            {categories.map(({ title, value }, i) => (
              <Section labelWidth={sectionWidth} label={title} key={`category-${i}`}>
                <Typography variant="body2" color='text.primary' component="div">{value}</Typography>
              </Section>
            ))}
            {scores.map(({ title, score }, i) => (
              <Section labelWidth={sectionWidth} label={title} key={`score-${i}`}>
                <Typography variant="body2" color='text.primary' component="div">{score}</Typography>
              </Section>
            ))}
          </Box>
        )}

        {(isCareNet && kojin?.personalId) && (
          <Box>
            <BlockCaption
              caption='CareNet会員情報'
              rightElement={
                kojin.kojinCode ? (
                  <Typography variant="caption" color='text.primary' component="div">PID: {kojin.personalId}</Typography>
                ) : <></>
              }
            />
            {kojin.primarySinryoka && (
              <Section labelWidth={sectionWidth} label="診療科(主)">
                <Typography variant="body2" color='text.primary' component="div">{kojin.primarySinryoka.sinryokaName}</Typography>
              </Section>
            )}
            {/* kojin.comSinryoka ? (
              <Section minWidth={sectionWidth} label="診療科">
                <Stack direction='row' spacing={1} flexWrap="wrap" useFlexGap>
                  {kojin.comSinryoka.map((sinryoka, i) => sinryoka.sinryokaCode === kojin.primarySinryoka?.sinryokaCode ? (
                    <Typography key={`sinryoka-${i}`} variant="body2" color='text.primary' component="div" fontWeight="bold">{sinryoka.sinryokaName}</Typography>
                  ) : (
                    <Typography key={`sinryoka-${i}`} variant="body2" color='text.primary' component="div">{sinryoka.sinryokaName}</Typography>
                  ))}
                </Stack>
              </Section>
            ) : <></> */}

            {kojin.kanriBunrui ? (
              <Section labelWidth={sectionWidth} label="勤務先分類">
                <KanriBunruiText kanriBunrui={kojin.kanriBunrui}/>
              </Section>
            ) : <></> }

            {(kojin.eMail && kojin.eMail.trim() !== '') && (
              <Section labelWidth={sectionWidth} label="email">
                <MailAddress address={kojin.eMail} mailStatus={kojin.mailStatus}/>
              </Section>
            )}

            {kojin.mrpStats && (
              <Section labelWidth={sectionWidth} label="MRPlus">
                <Stack direction='row' spacing={1} alignItems="center">
                  <ValueSpan text={numberFormat(kojin.mrpStats.visitCount, '-')}/>
                  <Typography variant="body2" color='text.primary' component="div">/</Typography>
                  <ValueSpan text={numberFormat(kojin.mrpStats.assignCount, '-')} suffix='案件'/>
                  <ValueSpan prefix="(" text={`${round((kojin.mrpStats.visitRate || 0), 2)}`} suffix='%)'/>
                  <Chip label={kojin.mrpStats.rank.name} size="small"/>
                </Stack>
                <Stack direction='row' spacing={1} alignItems="center">
                  {(kojin.mrpStats.commentCount ?? 0) > 0 && (
                    <ValueSpan prefix='コメント' text={numberFormat(kojin.mrpStats.commentCount, '-')} suffix='件'/>
                  )}
                  <ValueSpan prefix='平均評価' body={<Rating readOnly defaultValue={kojin.mrpStats.evaluationAverage ?? 0} precision={0.1} size='small'/>}/>
                  <ValueSpan text={`${round(kojin.mrpStats.evaluationAverage ?? 0, 2)}`} suffix='点'/>
                  <ValueSpan prefix='(' text={numberFormat(kojin.mrpStats.evaluationCount, '-')} suffix='回)'/>
                </Stack>
              </Section>
            )}

            {kojin.academia && (
              <Section labelWidth={sectionWidth} label="Academia">
                <Stack direction="row" flexWrap="wrap" spacing={1} alignItems="center">
                  <Typography variant="body2" color='text.secondary' component="div">直近１ヶ月</Typography>
                  <ValueSpan prefix="閲覧回数" text={numberFormat(kojin.academia.pageView)} suffix="回"/>
                  <ValueSpan prefix="👍️回数" text={numberFormat(kojin.academia.interestCount)} suffix="回"/>
                </Stack>
                <Stack direction="row" flexWrap="wrap" spacing={1} alignItems="center">
                  <Typography variant="body2" color='text.secondary' component="div">登録時の選択</Typography>
                  {kojin.academia.sinryoka ? (
                    <Typography variant="body2" color='text.primary' component="div">{kojin.academia.sinryoka.name}</Typography>
                  ) : <></>}

                  {kojin.academia.diseases?.map((disease, i) => (
                    <Typography key={`ac-disease-${i}`} variant="body2" color='text.primary' component="div">{disease.name}</Typography>
                  ))}
                </Stack>
              </Section>
            )}

            {(kojin.diseases && kojin.diseases.length > 0) ? (
              <Section labelWidth={sectionWidth} label="関心疾患" disablePadding>
                <KojinDiseasesBlock
                  diseases={kojin.diseases}
                  limit={10}
                />
              </Section>
            ) : <></>}
          </Box>
        )}
      </Stack>
    </>
  )
}
